import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import * as jsonManager from '../../abis/Manager.json';
import { environment } from '../../../environments/environment';
import { ConnectionsService } from '../../services/connectionsWeb3/connections.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { PaymentService } from '../../services/payment.service';
import { UserExternalInvestDto } from 'src/app/interfaces/user-external-invest.dto';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-buy-batch-through-company',
  templateUrl: './buy-batch-through-company.component.html',
  styleUrls: ['./buy-batch-through-company.component.css'],
})
export class BuyBatchThroughCompanyComponent implements OnInit {
  formBuyBatchThroughCompany: FormGroup = this.createFormBuyThroughCompany();

  ourAddress: string | undefined;
  web3: any;
  abiManager: any = jsonManager;
  contractManager: any;
  availableTokens: {
    tokensAddress: string;
    nameTokens: string;
    sellerAddress?: string;
  }[] = [];
  tokenToSellerMap: { [tokenAddress: string]: string } = {};
  mappingId = new Map();

  investments: UserExternalInvestDto[] = [];
  selectedTokenAddress: string | undefined;
  verified: boolean = false;
  invested: boolean = false;
  blockVerify: boolean = true;
  blockInvest: boolean = true;
  externalCheck: string = '';
  isExternalChecked: boolean = false;
  externalCanReserve: boolean = false;
  nonExternalCanProceed: boolean = true;
  readonly investmentTypeExternal = 'external';
  readonly investmentTypeFailedMint = 'failed-mint';
  readonly investmentTypeNoChargeTx = 'no-charge-tx';
  private readonly specialOriginFundsOptions = ['payment gateway', 'bank'] as const;

  get showPrivateSaleCheckbox(): boolean {
    const type = this.investmentType?.value;
    return type === this.investmentTypeExternal || type === this.investmentTypeNoChargeTx;
  }

  constructor(
    private readonly connSrv: ConnectionsService,
    private readonly afs: AngularFirestore,
    private readonly paymentService: PaymentService
  ) {
    this.web3 = this.connSrv.web3Instance;

    this.connSrv.addressUser.subscribe((res: string) => {
      this.ourAddress = res;
    });

    this.contractManager = new this.web3.eth.Contract(
      this.abiManager.abi,
      environment.config.ADDRESS_MANAGER
    );
  }

  ngOnInit() {
    this.loadTokens();

    this.tokenAddress?.valueChanges.subscribe((selectedTokenAddress) => {
      const token = this.availableTokens.find(
        (token) => token.tokensAddress === selectedTokenAddress
      );
      this.selectedTokenAddress = token?.nameTokens;
      this.verifyStatus(true);
      this.onChangeInvestmentType({ target: { value: this.investmentType?.getRawValue() } });
    });

    this.investmentType?.valueChanges.subscribe(type => {
      this.onChangeInvestmentType({ target: { value: type } });
    });

    this.investments = [];
    this.onListInvestmentsChange(this.investments);
  }

  verifyStatus(forceChange: boolean = false) {
    if (forceChange) {
      this.verified = false;
      this.invested = false;
      if (this.isExternalChecked) {
        this.externalCanReserve = false;
      }
    }
    this.blockVerify =
      !this.selectedTokenAddress ||
      this.investments.length == 0 ||
      this.verified;
    this.blockInvest =
      !this.selectedTokenAddress ||
      this.investments.length == 0 ||
      !this.verified ||
      this.invested ||
      (this.isExternalChecked && !this.externalCanReserve);
  }

  createFormBuyThroughCompany() {
    return new FormGroup({
      tokenAddress: new FormControl('', [Validators.required]),
      investmentType: new FormControl('', [Validators.required]),
      isPrivateSale: new FormControl(false),
    });
  }

  get tokenAddress() {
    return this.formBuyBatchThroughCompany?.get('tokenAddress');
  }

  get investmentType() {
    return this.formBuyBatchThroughCompany?.get('investmentType');
  }

  get isPrivateSale() {
    return this.formBuyBatchThroughCompany?.get('isPrivateSale');
  }

  onListInvestmentsChange(investments: UserExternalInvestDto[]) {
    this.investments = investments;
    this.verifyStatus(true);
    this.onChangeInvestmentType({ target: { value: this.investmentType?.getRawValue() } });
  }

  onChangeInvestmentType(event: any) {
    const selectedType = event.target.value;
    this.isExternalChecked = selectedType === this.investmentTypeExternal;
    this.externalCanReserve = false;
    this.nonExternalCanProceed = false;
    this.externalCheck = '';

    if (this.isExternalChecked) {
      const tokenName = this.selectedTokenAddress;
      if (!tokenName) {
        this.externalCheck = 'Debe seleccionar un proyecto para verificar inversión externa';
        this.verifyStatus();
        return;
      }
      if (this.investments.length === 0) {
        this.externalCheck = 'Debe agregar inversiones para verificar una inversión externa';
        this.verifyStatus();
        return;
      }
      // Validar que todas las inversiones tengan tokens > 0
      const invalidInvestments = this.investments.filter(inv => !inv.tokens || inv.tokens <= 0);
      if (invalidInvestments.length > 0) {
        this.externalCheck = 'Todas las cantidades de tokens deben ser mayores que 0';
        this.verifyStatus();
        return;
      }
      // Llamar automáticamente a la verificación
      this.paymentService.checkExternalBatchInvestments(this.investments, tokenName).then((res: any) => {
        this.investments = res.investments;
        if (res.error === 'no fondos' && res.accountToken != null && res.accountBalance != null) {
          const magnitude = Math.max(Math.abs(res.accountToken), Math.abs(res.accountBalance));
          if (res.accountToken - res.accountBalance <= magnitude * 1e-9) {
            res.error = '';
          }
        }
        if (res.error === '') {
          this.externalCanReserve = true;
          const totalTokens = this.investments.reduce((sum, inv) => sum + (inv.tokens || 0), 0);
          this.externalCheck = `Verificación exitosa. Total: ${totalTokens} tokens listos para reservar.`;
          this.verified = true;
        } else {
          this.externalCheck = this.getCheckText(res.error);
          this.externalCanReserve = false;
          this.verified = false;
        }
        this.verifyStatus();
      }).catch((error) => {
        console.error('Error al verificar inversiones externas:', error);
        this.externalCheck = 'Error al verificar las inversiones externas';
        this.externalCanReserve = false;
        this.verified = false;
        this.verifyStatus();
      });
    } else if (selectedType) {
      this.checkNonExternalAvailability();
    }
  }

  private checkNonExternalAvailability() {
    const tokenAddr = this.tokenAddress?.getRawValue();
    const tokenName = this.selectedTokenAddress;

    if (!tokenAddr || !tokenName) {
      this.externalCheck = 'Debe completar todos los campos para validar tokens disponibles';
      this.nonExternalCanProceed = false;
      this.verifyStatus();
      return;
    }

    if (this.investments.length === 0) {
      this.externalCheck = 'Debe agregar inversiones para validar disponibilidad';
      this.nonExternalCanProceed = false;
      this.verifyStatus();
      return;
    }

    const invalidInvestments = this.investments.filter(inv => !inv.tokens || inv.tokens <= 0);
    if (invalidInvestments.length > 0) {
      this.externalCheck = 'Todas las cantidades de tokens deben ser mayores que 0';
      this.nonExternalCanProceed = false;
      this.verifyStatus();
      return;
    }

    const tokenId = this.mappingId.get(tokenAddr);
    if (!tokenId) {
      this.externalCheck = 'Token no encontrado';
      this.nonExternalCanProceed = false;
      this.verifyStatus();
      return;
    }

    const totalTokensNeeded = this.investments.reduce((sum, inv) => sum + (inv.tokens || 0), 0);

    this.afs.collection('investments').doc(tokenId).get().subscribe((doc: any) => {
      if (!doc?.exists) {
        this.externalCheck = 'Proyecto no encontrado';
        this.nonExternalCanProceed = false;
        this.verifyStatus();
        return;
      }

      const data = doc.data();
      const availableTokens = (data?.amountToSell || 0) - (data?.amountSold || 0) - (data?.reserves || 0);

      if (availableTokens < totalTokensNeeded) {
        this.externalCheck = `No hay suficientes tokens disponibles. Disponibles: ${availableTokens}, Necesarios: ${totalTokensNeeded}`;
        this.nonExternalCanProceed = false;
      } else {
        this.externalCheck = `Tokens disponibles verificados correctamente. Total: ${totalTokensNeeded} tokens`;
        this.nonExternalCanProceed = true;
      }
      this.verifyStatus();
    });
  }

  private loadTokens() {
    this.afs
      .collection('investments')
      .get()
      .subscribe(
        (querySnapshot) => {
          this.availableTokens = querySnapshot.empty
            ? []
            : querySnapshot.docs
                .map((doc) => {
                  const data = doc.data() as {
                    tokensAddress?: string;
                    nameTokens?: string;
                    sellerAddress?: string;
                    id: string;
                  };
                  if (data.tokensAddress && data.sellerAddress) {
                    this.tokenToSellerMap[data.tokensAddress] =
                      data.sellerAddress;
                  }
                  this.mappingId.set(data.tokensAddress, data.id);
                  return {
                    tokensAddress: data.tokensAddress ?? '',
                    nameTokens: data.nameTokens ?? '',
                    sellerAddress: data.sellerAddress ?? '',
                  };
                })
                .filter(
                  (token) =>
                    token.tokensAddress.trim() !== '' &&
                    token.nameTokens.trim() !== '' &&
                    token.sellerAddress?.trim() !== ''
                );

          console.log('Tokens disponibles:', this.availableTokens);
          console.log('Mapa token -> seller:', this.tokenToSellerMap);
        },
        (error) => {
          console.error('Error al cargar los tokens: ', error);
          this.availableTokens = [];
        }
      );
    console.log('Mapping ID:', this.mappingId);
    console.log('available Tokens:', this.availableTokens);
  }

  formatToWei(num: number) {
    return this.web3.utils.toWei(num.toString(), 'ether');
  }

  getCheckText(error: string = ''): string {
    if (error === 'no activo') {
      return 'El proyecto no está activo';
    } else if (error === 'no configurado') {
      return 'Error en la configuración del sistema';
    } else if (error === 'no existe') {
      return 'La billetera del sistema no existe en Mangopay';
    } else if (error === 'user error') {
      return 'Hay errores en la lista de inversiones';
    } else if (error === 'no fondos') {
      return 'No hay liquidez suficiente en la billetera del sistema';
    } else if (error === 'no disponibilidad') {
      return 'No hay participaciones suficientes para realizar las inversiones';
    } else if (error === 'no token') {
      return 'No se encontró el proyecto seleccionado';
    }

    return '';
  }

  async checkInvestments() {
    // Si es inversión externa, verificar con el servicio de inversiones externas
    if (this.isExternalChecked) {
      Swal.fire({
        title: 'Verificando...',
        html: 'No cierres esta pantalla!!',
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const result = await this.paymentService.checkExternalBatchInvestments(
          this.investments,
          this.selectedTokenAddress!
        );

        Swal.close();

        this.investments = result.investments;
        if (result.error === 'no fondos' && result.accountToken != null && result.accountBalance != null) {
          const magnitude = Math.max(Math.abs(result.accountToken), Math.abs(result.accountBalance));
          if (result.accountToken - result.accountBalance <= magnitude * 1e-9) {
            result.error = '';
          }
        }

        if (result.error === '') {
          this.verified = true;
          this.externalCanReserve = true;
          const totalTokens = this.investments.reduce((sum, inv) => sum + (inv.tokens || 0), 0);
          this.externalCheck = `Verificación exitosa. Total: ${totalTokens} tokens listos para reservar.`;
          Swal.fire({
            title: 'Verificación exitosa',
            text: 'Las inversiones han sido verificadas correctamente.',
            icon: 'success',
          });
        } else {
          this.externalCheck = this.getCheckText(result.error);
          this.externalCanReserve = false;
          this.verified = false;
          Swal.fire({
            title: 'Error de verificación',
            text: this.getCheckText(result.error),
            icon: 'error',
          });
        }
      } catch (error) {
        console.error('Error al verificar inversiones:', error);
        Swal.close();
        this.externalCheck = 'Error al verificar las inversiones externas';
        this.externalCanReserve = false;
        this.verified = false;
        Swal.fire({
          title: 'Error',
          text: 'Ocurrió un error al verificar las inversiones.',
          icon: 'error',
        });
        this.verifyStatus();
        return;
      }
    } else {
      // Si NO es inversión externa, validar usuarios antes de permitir inversión
      Swal.fire({
        title: 'Verificando...',
        html: 'No cierres esta pantalla!!',
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const validationResult = await this.validateUsers();

        Swal.close();

        if (validationResult.errors.length === 0) {
          this.verified = true;
          Swal.fire({
            title: 'Verificación exitosa',
            text: 'Todas las inversiones han sido verificadas correctamente.',
            icon: 'success',
          });
        } else {
          this.verified = false;
          // Mostrar errores en el mismo formato que con checkbox
          const errorMessages = validationResult.errors.map(err => err).join(', ');
          Swal.fire({
            title: 'Error de verificación',
            html: `<p>Hay errores en la lista de inversiones:</p><ul>${validationResult.errors.map((err) => `<li>${err}</li>`).join('')}</ul>`,
            icon: 'error',
          });
        }
      } catch (error) {
        console.error('Error al verificar inversiones:', error);
        Swal.close();
        this.verified = false;
        Swal.fire({
          title: 'Error',
          text: 'Ocurrió un error al verificar las inversiones.',
          icon: 'error',
        });
      }
    }

    this.verifyStatus();
  }

  /**
   * Valida que todos los usuarios de las inversiones existan y cumplan los requisitos
   * Retorna un objeto con los errores encontrados
   */
  private async validateUsers(): Promise<{ errors: string[] }> {
    const errors: string[] = [];

    if (this.investments.length === 0) {
      errors.push('Debe agregar al menos una inversión');
      return { errors };
    }

    // Validar que todas las inversiones tengan tokens > 0
    const invalidInvestments = this.investments.filter(inv => !inv.tokens || inv.tokens <= 0);
    if (invalidInvestments.length > 0) {
      errors.push('Todas las cantidades de tokens deben ser mayores que 0');
      return { errors };
    }

    // Validar cada usuario
    for (const investment of this.investments) {
      try {
        // Buscar el usuario por email
        const userQuery = await firstValueFrom(
          this.afs
            .collection('users', (ref) => ref.where('email', '==', investment.email).limit(1))
            .get()
        );

        if (!userQuery || userQuery.empty || !userQuery.docs[0]?.exists) {
          errors.push(`${investment.email}: Usuario no encontrado`);
          continue;
        }

        const userData: any = userQuery.docs[0].data();

        // Verificar KYC
        if (userData?.kyc?.result != 1) {
          errors.push(`${investment.email}: Usuario no tiene KYC completado`);
          continue;
        }

        // Verificar país de residencia
        if (
          (!userData?.particular || !userData?.particular?.pais_de_residencia) &&
          !userData?.Empresa?.Representante?.pais_de_residencia
        ) {
          errors.push(`${investment.email}: Usuario con irregularidades`);
          continue;
        }
      } catch (error: any) {
        console.error(`Error validando usuario ${investment.email}:`, error);
        errors.push(`${investment.email}: ${error.message || 'Error desconocido'}`);
      }
    }

    return { errors };
  }

  async processInvestments() {
    const selectedInvestmentType = this.investmentType?.getRawValue();
    
    if (!selectedInvestmentType) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Debe seleccionar un tipo de operación',
      });
      return;
    }

    if (this.isExternalChecked && !this.externalCanReserve) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Debe verificar las inversiones externas antes de procesar',
      });
      return;
    }

    if (!this.isExternalChecked && !this.nonExternalCanProceed) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Debe verificar la disponibilidad de tokens antes de procesar',
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: 'Procesar inversiones',
      html: `¿Estás seguro de que quieres procesar las inversiones en <strong>${this.selectedTokenAddress}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, procesar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    let originFunds: 'payment gateway' | 'bank' | null | undefined;
    if (selectedInvestmentType === this.investmentTypeFailedMint) {
      originFunds = await this.askSpecialOriginFunds();
      if (!originFunds) {
        return;
      }
    }

    Swal.fire({
      title: 'Procesando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const tokenAddr = this.tokenAddress?.getRawValue();
    if (!tokenAddr || !this.mappingId.has(tokenAddr)) {
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Token no encontrado',
      });
      this.verifyStatus();
      return;
    }

    const tokenId = this.mappingId.get(tokenAddr);
    const isPrivateSale = this.isPrivateSale?.value || false;

    // Todas las operaciones batch usan mintTokenWithoutPay
    try {
      let mintMode: 'mint-only' | 'no-charge-tx' | undefined;
      let isExternal = false;
      
      if (selectedInvestmentType === this.investmentTypeExternal) {
        // Para inversiones externas, no necesitamos mintMode
        isExternal = true;
        originFunds = 'payment gateway';
      } else if (selectedInvestmentType === this.investmentTypeFailedMint) {
        mintMode = 'mint-only';
      } else if (selectedInvestmentType === this.investmentTypeNoChargeTx) {
        mintMode = 'no-charge-tx';
        originFunds = 'bank';
      } else {
        Swal.close();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Tipo de operación no válido',
        });
        return;
      }

      const result: any = await this.paymentService.directBatchMintInvestment({
        investments: this.investments,
        projectId: tokenId,
        mintMode: mintMode,
        isExternal: isExternal,
        originFunds: originFunds ?? undefined,
        isPrivateSale: isPrivateSale
      });

      Swal.close();

      if (result.success) {
        this.verified = true;
        this.invested = true;
        const batchCount = this.investments.length; // guardar antes de resetear
        this.investmentType?.setValue('');
        this.formBuyBatchThroughCompany.reset();
        this.investments = [];
        this.onListInvestmentsChange(this.investments);

        // El backend procesa el batch en segundo plano (fire-and-forget).
        // No se disponen de resultados individuales en tiempo real.
        Swal.fire({
          title: 'Inversiones en proceso',
          html: `<p>El batch de <strong>${batchCount}</strong> inversiones ha sido recibido y se está procesando en segundo plano.</p><p>Puedes cerrar esta pantalla. Los tokens se acreditarán automáticamente.</p>`,
          icon: 'success',
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: result.error || 'Error al procesar las inversiones',
          icon: 'error',
        });
      }
    } catch (error: any) {
      console.error('Error procesando inversiones batch:', error);
      Swal.close();
      Swal.fire({
        title: 'Error',
        text: error.message || 'Ocurrió un error al procesar las inversiones.',
        icon: 'error',
      });
    }

    this.verifyStatus();
  }

  private async askSpecialOriginFunds(): Promise<'payment gateway' | 'bank' | null> {
    const result = await Swal.fire({
      title: 'Origen de fondos',
      input: 'select',
      inputOptions: {
        'payment gateway': 'Payment gateway',
        bank: 'Bank',
      },
      inputPlaceholder: 'Selecciona el origen',
      showCancelButton: true,
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value || !this.specialOriginFundsOptions.includes(value as 'payment gateway' | 'bank')) {
          return 'Debes seleccionar un origen de fondos';
        }
        return null;
      },
    });

    return result.isConfirmed ? (result.value as 'payment gateway' | 'bank') : null;
  }
}
